import type { ModelDefinition } from '../shared/model'

/** Status values returned by the Offline Task API. */
export type OfflineTaskStatus = 'submitted' | 'processing' | 'completed' | 'error'

/** Output quality accepted by the Offline Task API. */
export type OfflineTaskQuality = 'sd' | 'hd'

/** Frame rates accepted by the Offline Task API. */
export const OFFLINE_TASK_SUPPORTED_FPS = [
  8,
  10,
  12,
  15,
  16,
  18,
  20,
  22,
  24,
  25,
  30,
  45,
  48,
  50,
  60,
  72,
  90,
  100,
  120,
] as const

export type OfflineTaskFps = (typeof OFFLINE_TASK_SUPPORTED_FPS)[number]

/** @deprecated Use {@link ModelDefinition}. */
export type OfflineModel = ModelDefinition

/** Model factory exposed through the shared `models` entry point. */
export interface OfflineModels {
  /** Select a model for offline generation. */
  offline(name: string): ModelDefinition
}

export type OfflineTaskResult = {
  result_url?: string | null
  upload_error?: string | null
  finalize_duration_ms?: number
  compression_level?: string | null
  frame_count?: number
  fps?: number
  result_duration_ms?: number
  file_size_bytes?: number
  upload_status?: string | null
  dropped_frame_count?: number
  original_file_size_bytes?: number
} & Record<string, unknown>

/** Complete task object returned by create, query, batch query, and list calls. */
export type OfflineTask = {
  uid: string
  userUid?: string
  prompt: string
  refImagePath: string | null
  refVideoPath: string
  processorId?: string | null
  quality: OfflineTaskQuality
  requestedFps?: number | null
  retryCount?: number
  resolvedFps?: number
  videoDurationSeconds?: number
  billableDurationSeconds?: number
  chargePoints?: number
  status: OfflineTaskStatus
  result: OfflineTaskResult | null
  processStartTime?: string | null
  createTimestamp?: string
  updateTimestamp?: string
} & Record<string, unknown>

/** A local browser File is uploaded automatically; strings must be Xmax upload URLs. */
export type OfflineMediaInput = File | string

/** Generation context for one offline video task. */
export type OfflineContext = {
  /** Generation instructions. Leading/trailing whitespace is removed. */
  prompt: string
  /**
   * Optional reference image. Local Files are uploaded automatically; strings
   * must be URLs previously returned by `client.files.uploadImage()`.
   */
  refImageUrl?: OfflineMediaInput | null
}

export type OfflineSubmitOptions = {
  /** Model definition; this client method runs it as an offline task. */
  model: ModelDefinition
  /** Source video File or a URL previously returned by `client.files.uploadVideo()`. */
  source: OfflineMediaInput
  /** Generation prompt and optional reference image. */
  context: OfflineContext
  /** @default 'hd' */
  quality?: OfflineTaskQuality
  /** Omit to use the source video's detected frame rate. */
  fps?: OfflineTaskFps
  /** Cancels local upload, request, and polling work. It does not cancel an accepted server task. */
  signal?: AbortSignal
}

export type OfflineGenerateOptions = OfflineSubmitOptions & {
  /** Called for the submitted task and whenever its status changes. */
  onStatusChange?: (task: OfflineTask) => void
  /** Delay between status requests. @default 2000 */
  pollIntervalMs?: number
  /** Maximum time for submit plus polling. @default 1800000 (30 minutes) */
  timeoutMs?: number
}

export type OfflineRequestOptions = {
  signal?: AbortSignal
}

export type OfflineBatchQueryResult = {
  list: OfflineTask[]
}

export type OfflineListOptions = OfflineRequestOptions & {
  /** @default 1 */
  pageNumber?: number
  /** @default 10; maximum 100 */
  pageSize?: number
  status?: OfflineTaskStatus
}

export type OfflineTaskPage = {
  pageNumber: number
  pageSize: number
  total: number
  list: OfflineTask[]
}

export type OfflineClient = {
  /** Submit a task and poll it until `completed` or `error`. */
  generate: (options: OfflineGenerateOptions) => Promise<OfflineTask>
  /** Submit an asynchronous offline video task. Local Files are uploaded first. */
  submit: (options: OfflineSubmitOptions) => Promise<OfflineTask>
  /** Retrieve one task by UID. */
  status: (taskUid: string, options?: OfflineRequestOptions) => Promise<OfflineTask>
  /** Retrieve up to 100 tasks in one request. */
  batchQuery: (
    taskUids: readonly string[],
    options?: OfflineRequestOptions,
  ) => Promise<OfflineBatchQueryResult>
  /** List the current user's tasks. */
  list: (options?: OfflineListOptions) => Promise<OfflineTaskPage>
}
